// oxlint-disable typescript/no-explicit-any
import { decode } from 'html-entities'

import { defaultCmsContent } from './cms-defaults'

export { cmsDefaults } from './cms-default-data'

/**
 * Important: This function is used in both a browser and a
 * Node.js environment. We need to try and find the CMS URL
 * in the following order:
 *
 * 1. globalThis.cmsUrl
 * 2. process.env.CMS_CONTENT_URL
 */

function getCmsUrl() {
  if (typeof globalThis !== 'undefined' && (globalThis as any).cmsUrl) {
    return `${(globalThis as any).cmsUrl}`
  }

  if (typeof process !== 'undefined' && process.env.CMS_CONTENT_URL) {
    return process.env.CMS_CONTENT_URL
  }

  throw new Error('Could not find CMS URL')
}

export type CmsKey = keyof typeof defaultCmsContent
export type CmsContent = typeof defaultCmsContent

export type CmsResponse = {
  Items: Array<{
    tool: string
    key: string
    value: string
    group: string | null
  }>
}

/**
 * Get the value of a CMS key.
 */
export const getCmsValue = async (key: CmsKey, opts?: GetCmsContentOpts) => {
  const content = await getCmsContent(opts)
  return content[key]
}

/**
 * Get the default CMS content.
 */
export const getCmsDefaults = () => {
  return { ...defaultCmsContent }
}

/**
 * Get all CMS keys.
 */
export const getCmsKeys = () => {
  return Object.keys(getCmsDefaults())
}

type GetCmsContentOpts = {
  useDefaults?: boolean
  fetchOpts?: RequestInit
}

/**
 * Get all CMS content.
 *
 * Note: If the request fails, the default content is returned.
 */
export const getCmsContent = async (opts?: GetCmsContentOpts) => {
  if (opts?.useDefaults) {
    return getCmsDefaults()
  }

  try {
    const res = await fetch(getCmsUrl(), opts?.fetchOpts)

    if (!res.ok) {
      throw new Error('Could not fetch CMS content')
    }

    const body: CmsResponse = await res.json()
    return mergeCmsContentWithDefaults(body)
  } catch {
    console.info('Failed to fetch CMS content. Returning default content.')
    return getCmsDefaults()
  }
}

/**
 * Merge the fetched CMS content with the
 * default content.
 */
export const mergeCmsContentWithDefaults = (cmsContent: CmsResponse) => {
  const validCmsKeys = Object.keys(getCmsDefaults())
  const content = cmsContent.Items.filter(({ key }) =>
    validCmsKeys.includes(key),
  )

  // Merge the fetched content with the default content.
  return content.reduce((acc, { key, value }) => {
    acc[key as CmsKey] = decode(value)
    return acc
  }, getCmsDefaults())
}

/**
 * Get the status of all CMS keys. Used to check
 * if the CMS keys are defined in the defaults and
 * vice versa.
 */
export const getCmsKeysStatus = async (opts?: GetCmsContentOpts) => {
  const res = await fetch(getCmsUrl(), {
    ...opts?.fetchOpts,
    cache: 'no-store',
  })

  if (!res.ok) {
    return []
  }

  const cmsContent: CmsResponse = await res.json()

  const defaultCmsKeys = Object.keys(getCmsDefaults())
  const optimizelyCmsKeys = cmsContent.Items.map(({ key }) => key)

  const allCmsKeys = Array.from(
    new Set([...defaultCmsKeys, ...optimizelyCmsKeys]),
  )

  return allCmsKeys.map(key => {
    return {
      id: key,
      key,
      existsInDefault: defaultCmsKeys.includes(key),
      existsInCms: optimizelyCmsKeys.includes(key),
    }
  })
}
/**
 * Get the status and values of all CMS keys. Used to check
 * if the CMS values are the same in both defaults and the CMS.
 */
export async function getCmsKeysStatusAndValues(opts?: GetCmsContentOpts) {
  const keysStatus = await getCmsKeysStatus(opts)
  const cmsContent = await getCmsContent(opts)

  return keysStatus.map(status => {
    const defaultValue = getCmsDefaults()[status.key as CmsKey]
    const cmsValue = cmsContent[status.key as CmsKey]

    return {
      ...status,
      defaultValue,
      cmsValue,
    }
  })
}
